import React, { useCallback } from 'react';

import ClinicalNotePainSection from './ClinicalNotePainSection.jsx';
import ClinicalNoteRomSection from './ClinicalNoteRomSection.jsx';
import ClinicalNoteStrengthSection from './ClinicalNoteStrengthSection.jsx';
import ClinicalNoteGoalSection from './ClinicalNoteGoalSection.jsx';
import ClinicalNoteContentSection from './ClinicalNoteContentSection.jsx';
import ClinicalNoteTreatmentSection from './ClinicalNoteTreatmentSection.jsx';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';

/**
 * Discharge-note workspace.
 *
 * The initial Pain section opens by default. Remaining documentation stays
 * minimized until the clinician needs it. Collapsing an accordion section
 * does not unmount the editor or discard its pending autosave.
 *
 * Discharge includes Pain, ROM, Strength/GMMT, Goals, Clinical Content,
 * and final Treatment. Special Tests, Palpation, and Plan of Care are
 * intentionally excluded from this form.
 *
 * The optional navigationCoordinator is forwarded to every editable
 * section so each section can register its pending-save barrier before
 * ClinicalNotePage changes the active visit.
 */
export default function ClinicalNoteDischargeForm({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const resetKey = String(clinicalNoteId ?? 'new');
  const accordionGroup = `clinical-note-${resetKey}`;

  const { statuses, refreshSectionStatuses } =
    useClinicalSectionStatuses(clinicalNoteId);

  const handleSaved = useCallback(
    async (...args) => {
      if (typeof onSaved === 'function') {
        await onSaved(...args);
      }
      await refreshSectionStatuses();
    },
    [onSaved, refreshSectionStatuses]
  );

  const commonProps = {
    clinicalNoteId,
    disabled,
    onSaved: handleSaved,
    onVersionChange,
    navigationCoordinator,
  };

  const sections = [
    'pain',
    'rom',
    'strength',
    'goals',
    'content',
    'treatment',
  ];

  const accordionProps = (key, index) => ({
    sectionKey: `${resetKey}-${key}`,
    accordionGroup,
    previousSectionKey:
      index > 0 ? `${resetKey}-${sections[index - 1]}` : null,
    nextSectionKey:
      index < sections.length - 1
        ? `${resetKey}-${sections[index + 1]}`
        : null,
  });

  return (
    <div className="clinical-note-section-stack">
      <ClinicalNoteCollapsibleSection
        title="Pain"
        defaultCollapsed={false}
        status={statuses.PAIN}
        resetKey={`${resetKey}-pain`}
        {...accordionProps('pain', 0)}
      >
        <ClinicalNotePainSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Range of Motion"
        defaultCollapsed
        status={statuses.ROM}
        resetKey={`${resetKey}-rom`}
        {...accordionProps('rom', 1)}
      >
        <ClinicalNoteRomSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Strength / GMMT"
        defaultCollapsed
        status={statuses.STRENGTH}
        resetKey={`${resetKey}-strength`}
        {...accordionProps('strength', 2)}
      >
        <ClinicalNoteStrengthSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Goals"
        defaultCollapsed
        status={statuses.GOAL}
        resetKey={`${resetKey}-goals`}
        {...accordionProps('goals', 3)}
      >
        <ClinicalNoteGoalSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Clinical Content / Narrative"
        defaultCollapsed
        status={statuses.CONTENT}
        resetKey={`${resetKey}-content`}
        {...accordionProps('content', 4)}
      >
        <ClinicalNoteContentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Final Treatment"
        defaultCollapsed
        status={statuses.TREATMENT}
        resetKey={`${resetKey}-treatment`}
        {...accordionProps('treatment', 5)}
      >
        <ClinicalNoteTreatmentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
    </div>
  );
}
